import React, { useState, memo, forwardRef, useImperativeHandle,  } from "react";
import {
  Box, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, ToggleButtonGroup, ToggleButton,
  Typography, Snackbar, Alert, Chip, CircularProgress,
  Button
} from "@mui/material";
import ThumbUpAltIcon from "@mui/icons-material/ThumbUpAlt";

import ThumbDownAltIcon from "@mui/icons-material/ThumbDownAlt";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";

import RocketLaunchIcon from "@mui/icons-material/RocketLaunch";
import LocalFireDepartmentIcon from "@mui/icons-material/LocalFireDepartment";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import { motion, AnimatePresence } from "framer-motion";
import { useAccount, useChainId } from "wagmi";
import { useAppKit } from "@reown/appkit/react";
import { db } from "../../components/firebase";
import { addDoc, collection, serverTimestamp } from "firebase/firestore";



/* ===== TOKENS ===== */
const GLASS = {
  bg: "linear-gradient(135deg, rgba(18,22,28,.92) 0%, rgba(10,13,17,.92) 100%)",
  border: "1px solid rgba(255,255,255,.12)",
  borderSoft: "1px solid rgba(255,255,255,.08)",
  borderHover: "1px solid rgba(255,255,255,.18)",
  shadow: "0 10px 32px rgba(2,8,23,.45)",
  text: "#e5e7eb",
  subtext: "#9ca3af",
};

const PRIMARY_GRADIENT = "linear-gradient(90deg, #2d74d7 0%, #14F195 100%)";
const PRIMARY_GRADIENT_HOVER = "linear-gradient(90deg, #2f86ff 0%, #20ffa9 100%)";

/* ===== FX ===== */
const FadeSlide = ({ children }) => (
  <motion.div initial={{ opacity: 0, y: 12, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: 12, scale: 0.98 }} transition={{ duration: 0.22, ease: "easeOut" }}>
    {children}
  </motion.div>
);

/* ===== Shared inputs ===== */
const inputSx = {
  mt: 2,
  "& .MuiInputLabel-root": { color: GLASS.subtext },
  "& .MuiOutlinedInput-root": {
    color: GLASS.text,
    background: "rgba(255,255,255,.03)",
    borderRadius: 2,
    "& fieldset": { borderColor: "rgba(255,255,255,.18)" },
    "&:hover fieldset": { borderColor: "rgba(255,255,255,.26)" },
    "&.Mui-focused fieldset": { borderColor: "rgba(255,255,255,.32)" },
  },
  "& .MuiFormHelperText-root": { color: "#ffb4b4" },
};

const toggleSx = {
  mt: 2,
  width: "100%",
  "& .MuiToggleButton-root": {
    flex: 1,
    color: GLASS.text,
    borderColor: "rgba(255,255,255,.18)!important",
    background: "rgba(255,255,255,.03)",
    "&:hover": { background: "rgba(255,255,255,.06)" },
    "&.Mui-selected": {
      background: "rgba(45,116,215,.18)",
      borderColor: "rgba(45,116,215,.36)!important",
    },
  },
};

const dialogPaper = {
  sx: { background: GLASS.bg, color: GLASS.text, border: GLASS.border, borderRadius: "12px", boxShadow: GLASS.shadow, overflow: "hidden" },
};

/* ===== Small UI bits ===== */
function Presets({ setAmount, disabled }) {
  const presets = ["5", "10", "25", "50", "100"];
  return (
    <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", mt: 1 }}>
      {presets.map((p) => (
        <Chip
          key={p}
          label={`$${p}`}
          onClick={() => setAmount(p)}
          disabled={disabled}
          clickable
          variant="outlined"
          sx={{
            borderColor: "rgba(255,255,255,.24)",
            color: GLASS.text,
            bgcolor: "rgba(255,255,255,.04)",
            "&:hover": { bgcolor: "rgba(255,255,255,.08)", borderColor: "rgba(255,255,255,.32)" },
            height: 26,
          }}
        />
      ))}
    </Box>
  );
}

function FeePreview({ amount, feeBps = 50 }) {
  const a = Number(amount || 0);
  const fee = Math.max(0, (a * feeBps) / 10000);
  const total = a + fee;
  return (
    <Box sx={{ mt: 1 }}>
      <Typography variant="caption" sx={{ color: GLASS.subtext }}>
        Platform fee ~ {(feeBps / 100).toFixed(2)}%
      </Typography>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, color: GLASS.text }}>
        <Typography variant="body2" sx={{ color: GLASS.subtext }}>Fee:</Typography>
        <Typography variant="body2">${fee.toFixed(2)}</Typography>
      </Box>
      <Box sx={{ display: "flex", justifyContent: "space-between", color: GLASS.text }}>
        <Typography variant="body2" sx={{ color: GLASS.subtext }}>Total:</Typography>
        <Typography variant="body2" fontWeight={700}>${total.toFixed(2)}</Typography>
      </Box>
    </Box>
  );
}

/* ==================== MAIN ==================== */
function ForecastActionsImpl({ forecastId, creatorId, compact = false, currency = 'prUSD' }, ref) {
  const { address } = useAccount();
  const chainId = useChainId();
  const { open: openAppKit } = useAppKit();

  const [stakeOpen, setStakeOpen] = useState(false);
  const [stakeSide, setStakeSide] = useState("YES");
  const [stakeAmount, setStakeAmount] = useState("");
  const [boostOpen, setBoostOpen] = useState(false);
  const [tipOpen, setTipOpen] = useState(false);
  const [toastState, setToastState] = useState({ open: false, type: "success", msg: "" });

  useImperativeHandle(ref, () => ({
    openStake: () => setStakeOpen(true),
    openBoost: () => setBoostOpen(true),
    openTip:   () => setTipOpen(true),
  }), []);

  const requireWallet = () => {
    if (!address) {
      setToastState({ open: true, type: "error", msg: "Connect wallet to continue." });
      try { openAppKit?.({ view: "Connect" }); } catch {}
      return false;
    }
    return true;
  };

  const saveAction = async (type, payload) => {
    await new Promise((r) => setTimeout(r, 600));
    await addDoc(collection(db, "promos", forecastId, "actions"), {
      type, payload, user: address || null, creatorId: creatorId || null, chainId: chainId || null,
      createdAt: serverTimestamp(), clientStatus: "mock",
    });
  };

  const onStakeConfirm = async ({ side, amount }) => { await saveAction("stake", { side, amount: Number(amount) }); setStakeAmount(""); setToastState({ open: true, type: "success", msg: "Stake saved (placeholder)." }); };
  const onBoostConfirm = async ({ amount }) => { await saveAction("boost", { amount: Number(amount) }); setToastState({ open: true, type: "success", msg: "Boost saved (placeholder)." }); };
  const onTipConfirm   = async ({ amount }) => { await saveAction("tip",   { amount: Number(amount) }); setToastState({ open: true, type: "success", msg: "Tip saved (placeholder)." }); };

  const chipBtnSx = {
  px: { xs: 0.5, sm: 0.9 },
  height: { xs: 20, sm: 24 },
  minWidth: { xs: 72, sm: 84 },         // ⟵ не даём ужаться сильнее, чтобы не было "Bo.."
  borderRadius: 1.5,
  border: "1px solid rgba(255,255,255,.18)",
  bgcolor: "rgba(255,255,255,.03)",
  color: GLASS.text,
  fontWeight: 900,
  fontSize: { xs: "0.60rem", sm: "0.74rem" }, // ⟵ ещё чуточку меньше на xs
  "& .MuiChip-label": { px: { xs: 0.35, sm: 0.6 }, whiteSpace: "nowrap" },
  "& .MuiChip-icon": { fontSize: { xs: 12, sm: 16 }, color: GLASS.text, mr: { xs: 0.25, sm: 0.5 } },
  "&:hover": { bgcolor: "rgba(255,255,255,.06)", borderColor: "rgba(255,255,255,.24)" },
  cursor: "pointer",
  lineHeight: 1.1,
};


  return (
    <>
      {/* MOBILE: Stake на отдельной строке; ниже — Boost+Tip 50/50. DESKTOP — в ряд. */}
{compact ? (
  <Box sx={{ display: "grid", gridTemplateColumns: "1fr", gap: 0.5 }}>
    {/* row 1: Stake — full width */}
    <Chip
      icon={<RocketLaunchIcon />}
      label="Stake"
      onClick={() => { if (!requireWallet()) return; setStakeOpen(true); }}
      sx={{
        ...chipBtnSx,
        width: "100%",
        justifyContent: "center",
        bgcolor: "rgba(45,116,215,.18)",
        borderColor: "rgba(45,116,215,.36)",
      }}
    />
    {/* row 2: Boost + Tip */}
<Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(72px, 1fr))", gap: 0.5 }}>
  <Chip
    icon={<LocalFireDepartmentIcon />}
    label="Boost"
    onClick={() => { if (!requireWallet()) return; setBoostOpen(true); }}
    sx={{ ...chipBtnSx, justifyContent: "center" }}
  />
  <Chip
    icon={<VolunteerActivismIcon />}
    label="Tip"
    onClick={() => { if (!requireWallet()) return; setTipOpen(true); }}
    sx={{ ...chipBtnSx, justifyContent: "center" }}
  />
</Box>

  </Box>
) : (
  <Box sx={{ display: "flex", gap: 0.8, flexWrap: "wrap" }}>
    <Chip
      icon={<RocketLaunchIcon />}
      label="Stake"
      onClick={() => { if (!requireWallet()) return; setStakeOpen(true); }}
      sx={{
        ...chipBtnSx,
        bgcolor: "rgba(45,116,215,.18)",
        borderColor: "rgba(45,116,215,.36)",
      }}
    />
    <Chip
      icon={<LocalFireDepartmentIcon />}
      label="Boost"
      onClick={() => { if (!requireWallet()) return; setBoostOpen(true); }}
      sx={chipBtnSx}
    />
    <Chip
      icon={<VolunteerActivismIcon />}
      label="Tip"
      onClick={() => { if (!requireWallet()) return; setTipOpen(true); }}
      sx={chipBtnSx}
    />
  </Box>
)}
      {/* Диалоги (как были) */}
      <StakeDialog
        open={stakeOpen}
        onClose={() => setStakeOpen(false)}
        onConfirm={onStakeConfirm}
        side={stakeSide}
        setSide={setStakeSide}
        amount={stakeAmount}
        setAmount={setStakeAmount}
        networkHint={null}
      />
      <AmountDialog open={boostOpen} onClose={() => setBoostOpen(false)} onConfirm={onBoostConfirm} title="Boost your forecast" cta="Boost" note={null} networkHint={null} />
      <AmountDialog open={tipOpen}   onClose={() => setTipOpen(false)}   onConfirm={onTipConfirm}   title="Send a tip"        cta="Send Tip" note={null} networkHint={null} />

      <Snackbar open={toastState.open} autoHideDuration={3200} onClose={() => setToastState({ ...toastState, open: false })}>
        <Alert severity={toastState.type} onClose={() => setToastState({ ...toastState, open: false })}
               sx={{ bgcolor: "rgba(18,22,28,.96)", color: GLASS.text, border: GLASS.border, boxShadow: GLASS.shadow, "& .MuiAlert-icon": { color: GLASS.text } }}>
          {toastState.msg}
        </Alert>
      </Snackbar>
    </>
  );
}

/* ===== Stake dialog ===== */
const StakeDialog = memo(function StakeDialog({ open, onClose, onConfirm, side, setSide, amount, setAmount, networkHint }) {
  const [step, setStep] = useState("form");
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    setError("");
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount"); return; }
    try { setStep("processing"); await onConfirm({ side, amount }); setStep("success"); }
    catch (e) { setError(e?.message || "Something went wrong"); setStep("form"); }
  };
  const closeAll = () => { setStep("form"); setError(""); onClose(); };

  return (
    <Dialog open={open} onClose={closeAll} fullWidth maxWidth="xs" PaperProps={dialogPaper}
            BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,.65)" } }}>
      <DialogTitle sx={{ color: GLASS.text }}>Stake on this Forecast</DialogTitle>
      <DialogContent>
        <AnimatePresence mode="wait">
          {step === "form" && (
            <FadeSlide key="form">
              {networkHint}
              <Typography variant="body2" sx={{ color: GLASS.subtext }}>
                Select the side and amount. Placeholder — on-chain later.
              </Typography>
              <ToggleButtonGroup exclusive value={side} onChange={(_, v) => v && setSide(v)} sx={toggleSx}>
                <ToggleButton value="YES"><ThumbUpAltIcon fontSize="small" sx={{ mr: 1 }} /> YES</ToggleButton>
                <ToggleButton value="NO"><ThumbDownAltIcon fontSize="small" sx={{ mr: 1 }} /> NO</ToggleButton>
              </ToggleButtonGroup>
              <TextField label="Amount (prUSD)" type="number" fullWidth value={amount} onChange={(e) => setAmount(e.target.value)}
                         inputProps={{ min: "0", step: "0.01" }} sx={inputSx} error={Boolean(error)} helperText={error || ""} autoFocus />
              <Presets setAmount={setAmount} disabled={false} />
              <FeePreview amount={amount} />
            </FadeSlide>
          )}
          {step === "processing" && (
            <FadeSlide key="processing">
              <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ color: GLASS.subtext }}>Saving your action…</Typography>
              </Box>
            </FadeSlide>
          )}
          {step === "success" && (
            <FadeSlide key="success">
              <Box sx={{ py: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                <CheckCircleRoundedIcon sx={{ color: "#14F195", fontSize: 56 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: GLASS.text }}>Stake saved</Typography>
                <Typography variant="body2" sx={{ color: GLASS.subtext }} align="center">
                  Recorded a stub. Confirm in the wallet in on-chain version.
                </Typography>
              </Box>
            </FadeSlide>
          )}
        </AnimatePresence>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === "form" ? (
          <>
            <Button onClick={closeAll} sx={{ color: GLASS.text }}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirm}
              sx={{ background: PRIMARY_GRADIENT, "&:hover": { background: PRIMARY_GRADIENT_HOVER } }}
              disabled={!amount || Number(amount) <= 0}>
              Confirm
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={closeAll}
            sx={{ background: PRIMARY_GRADIENT, "&:hover": { background: PRIMARY_GRADIENT_HOVER } }}>
            {step === "success" ? "Done" : "Close"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

/* ===== Amount dialog ===== */
const AmountDialog = memo(function AmountDialog({ open, onClose, onConfirm, title = "Enter amount", cta = "Confirm", note, networkHint }) {
  const [amount, setAmount] = useState("");
  const [step, setStep] = useState("form");
  const [error, setError] = useState("");

  const handleConfirm = async () => {
    setError("");
    if (!amount || Number(amount) <= 0) { setError("Enter a positive amount"); return; }
    try { setStep("processing"); await onConfirm({ amount }); setStep("success"); }
    catch (e) { setError(e?.message || "Something went wrong"); setStep("form"); }
  };
  const closeAll = () => { setAmount(""); setStep("form"); setError(""); onClose(); };

  return (
    <Dialog open={open} onClose={closeAll} fullWidth maxWidth="xs" PaperProps={dialogPaper}
            BackdropProps={{ sx: { backgroundColor: "rgba(0,0,0,.65)" } }}>
      <DialogTitle sx={{ color: GLASS.text }}>{title}</DialogTitle>
      <DialogContent>
        <AnimatePresence mode="wait">
          {step === "form" && (
            <FadeSlide key="form">
              {networkHint}
              {note && <Typography variant="body2" sx={{ color: GLASS.subtext, mb: 1 }}>{note}</Typography>}
              <TextField label="Amount (prUSD)" type="number" fullWidth value={amount} onChange={(e) => setAmount(e.target.value)}
                         inputProps={{ min: "0", step: "0.01" }} autoFocus error={Boolean(error)} helperText={error || ""} sx={inputSx} />
              <Presets setAmount={setAmount} disabled={false} />
              <FeePreview amount={amount} />
            </FadeSlide>
          )}
          {step === "processing" && (
            <FadeSlide key="processing">
              <Box sx={{ py: 4, display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
                <CircularProgress />
                <Typography variant="body2" sx={{ color: GLASS.subtext }}>Saving your action…</Typography>
              </Box>
            </FadeSlide>
          )}
          {step === "success" && (
            <FadeSlide key="success">
              <Box sx={{ py: 3, display: "flex", flexDirection: "column", alignItems: "center", gap: 1.5 }}>
                <CheckCircleRoundedIcon sx={{ color: "#14F195", fontSize: 56 }} />
                <Typography variant="h6" sx={{ fontWeight: 700, color: GLASS.text }}>Saved</Typography>
                <Typography variant="body2" sx={{ color: GLASS.subtext }} align="center">The stub is saved. On-chain later.</Typography>
              </Box>
            </FadeSlide>
          )}
        </AnimatePresence>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        {step === "form" ? (
          <>
            <Button onClick={closeAll} sx={{ color: GLASS.text }}>Cancel</Button>
            <Button variant="contained" onClick={handleConfirm}
              sx={{ background: PRIMARY_GRADIENT, "&:hover": { background: PRIMARY_GRADIENT_HOVER } }}
              disabled={!amount || Number(amount) <= 0}>
              {cta}
            </Button>
          </>
        ) : (
          <Button variant="contained" onClick={closeAll}
            sx={{ background: PRIMARY_GRADIENT, "&:hover": { background: PRIMARY_GRADIENT_HOVER } }}>
            {step === "success" ? "Done" : "Close"}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
});

const ForecastActions = forwardRef(ForecastActionsImpl);
export default memo(ForecastActions);